import _ from 'lodash';

/** A content action; shape comes from untyped backend content payloads. */
interface CloneableAction {
  /** Technical action name; rewritten to a unique name when cloning. */
  name: string;
  /** Own content of the action (only content actions have one); its `name` is kept in sync with the action name. */
  content?: { name: string; [key: string]: any };
  /** Backend payload carries more fields; passed through untouched. */
  [key: string]: any;
}

const CopyPasteUtil = {
  /**
   * Derive a unique clone name from an existing technical name.
   * Strips the trailing `_<timestamp>` part (if any) and appends `_<index>_<Date.now()>`.
   * @param name - Original technical name, parts separated by underscores.
   * @param index - Position of the item in its list, used to disambiguate clones created in the same millisecond.
   */
  createNewName(name: string | undefined, index: number | string = ''): string {
    const parts = name?.split('_');
    // replace date part
    // Non-null assertions: original code assumed `name` is a string here (undefined would throw the same way)
    if (parts!.length > 1) {
      parts!.pop();
    }
    return parts!.join('_') + `_${index}_${Date.now()}`;
  },
  /**
   * Rename all content actions in place so the clone gets unique action names.
   * @returns The same array with renamed actions, or undefined when `actions` is nullish.
   */
  cloneActions(actions: CloneableAction[] | undefined): CloneableAction[] | undefined {
    return actions?.map((action, index) => {
      if (!action.content) return action;
      const newName = this.createNewName(action.name, index);
      action.name = newName;
      if (action.content) {
        action.content.name = newName;
      }
      return action;
    });
  },
  /** Deep-clone a question object and re-name its actions. `any`: backend content payload shape. */
  cloneQuestion(question: any): any {
    const clonedQuestion = _.cloneDeep(question);

    if (clonedQuestion.actions) {
      clonedQuestion.actions = this.cloneActions(clonedQuestion.actions);
    }

    return clonedQuestion;
  },
  /** Deep-clone an answer object and re-name its actions. `any`: backend content payload shape. */
  cloneAnswer(answer: any): any {
    const clonedAnswer = _.cloneDeep(answer);

    if (clonedAnswer.actions) {
      clonedAnswer.actions = this.cloneActions(clonedAnswer.actions);
    }

    return clonedAnswer;
  },
  /** Deep-clone an answers array without renaming anything. */
  cloneAnswers(answers: any[]): any[] {
    return _.cloneDeep(answers);
  },
  /**
   *
   * Clone the content object, follow ups are not cloned.
   *
   * @param content - Content to clone (backend content box payload).
   */
  cloneContent(content: any): any {
    const clonedContent = _.cloneDeep(content);
    // set new box name
    clonedContent.box = this.createNewName(clonedContent.box);
    // copy all actions, find all properties with actions and clone them
    const allProperties = Object.keys(clonedContent);
    allProperties.forEach((property) => {
      if (clonedContent[property]?.actions) {
        clonedContent[property].actions = this.cloneActions(clonedContent[property].actions);
      }
    });

    return clonedContent;
  }
};

export default CopyPasteUtil;
